import { once } from 'lodash-es';

import { TrackEventName as EventName, TrackEvent as Event } from './event';
import { getBasicInfo } from '.';
import type { SensorsData } from './sdk/sensorsdata';

import { getSessionId } from '~/hook/initSession';
let sensorsdataPromise: Promise<any> | null = null;

export const loadSensorsdata = (): Promise<SensorsData | undefined> => {
  if (typeof window === 'undefined') return Promise.resolve(undefined);
  // @ts-ignore
  if (window.sensorsDataAnalytic201505) return Promise.resolve(window.sensorsDataAnalytic201505);
  if (sensorsdataPromise) return sensorsdataPromise;

  sensorsdataPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = '/sdk/sensors.js';
    script.async = true;
    // @ts-ignore
    script.onload = () => resolve(window.sensorsDataAnalytic201505);
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return sensorsdataPromise;
};

let lastReferrer = '';
let isTrackEnable = true;

export interface PageData {
  url: string;
  referrer?: string;
  title?: string;
}

const isHttpPrefix = /^http/;
const isClient = !import.meta.env.SSR;

const WHITE_LIST = [
  // Filter resizeObserver errors
  'resizeobserver loop',
  // Filter seed errors
  'seed in use',
  // Audio loading error
  'failed to start the audio device',
  // Image loading error
  'image load error!',
  // Insufficient balance
  'insufficient balance.',
  // UA change requires re-login
  'refresh please.',
  // Low-level browser errors, usually caused by no network or CORS failures from API errors
  'network error!',
];

async function trackPageView({ url, referrer = lastReferrer, title = '' }: PageData) {
  if (!url || !isTrackEnable) return;
  const origin = window.location.origin;
  lastReferrer = url;
  const $url = `${origin}${url}`;
  let $referrer = referrer;
  if (!isHttpPrefix.test(referrer)) {
    $referrer = `${origin}${referrer}`;
  }
  if (isClient) {
    const sensorsdata = await loadSensorsdata();
    sensorsdata?.quick('autoTrackSinglePage', {
      $url,
      $url_path: url,
      $referrer,
      $title: title,
    });
  }
}
const getAnonymousID = once(async () => {
  const sensorsdata = await loadSensorsdata();
  return new Promise<string>((resolve) => {
    sensorsdata?.quick('isReady', function () {
      resolve(sensorsdata.quick('getAnonymousID'));
    });
  });
});

async function trackEvent(key: string, options: object = {}) {
  if (!import.meta.env.SSR && isTrackEnable) {
    const sensorsdata = await loadSensorsdata();
    sensorsdata?.track(key, options);
  }
}

async function getPresetProperties() {
  if (!isClient || !isTrackEnable) return {};
  const sensorsdata = await loadSensorsdata();
  return sensorsdata?.getPresetProperties();
}

async function profileSetOnce(options?: any) {
  if (!isClient || !isTrackEnable) return;
  const props = await getPresetProperties();
  const sensorsdata = await loadSensorsdata();
  sensorsdata?.setOnceProfile({
    regist_utm_source: props?.['$latest_utm_source'],
    ...options,
  });
}

async function profileSet(options?: any) {
  if (!isClient || !isTrackEnable) return;
  const sensorsdata = await loadSensorsdata();
  sensorsdata?.setProfile({
    ...options,
  });
}

function trackException(err: Error, type: string = '') {
  if (!err || !err.message) return;
  if (type === '') {
    const stacks = err.stack && err.stack.split('\n');
    if (stacks && stacks.length > 2) {
      const first = stacks[1].replace(/.*\//, '');
      const last = stacks[stacks.length - 1].replace(/.*\//, '');
      type = `${last}->${first}`;
    }
  }
  // Filter by whitelist
  let msg = err.message.toLowerCase();
  for (let str of WHITE_LIST) {
    if (msg.search(str) !== -1) {
      return;
    }
  }
  //@ts-ignore
  const tip: string = err.tip;
  let message = err.message;
  if (tip) {
    message += `[${tip}]`;
  }

  trackEvent('error_notification', {
    error_content: message,
    error_type: type,
    error_id: '0',
  });
}

function autoTrackException() {
  window.addEventListener('error', (e) => {
    let err = e.error;
    let type = '';
    if (!err) {
      err = new Error(e.message);
      type = `${e.filename.replace(/.*\//, '')}:${e.lineno}:${e.colno}`;
    }
    // Filter :0:0 errors
    if (type === ':0:0') return;
    trackException(err, type);
  });
  window.addEventListener('unhandledrejection', (e) => {
    // Skip if no reason provided
    if (!e.reason) return;
    let err = e.reason;
    if (typeof err == 'string') {
      err = new Error(err);
    }
    trackException(err);
  });
}

async function getDistinctId() {
  if (import.meta.env.DEV) {
    return 'dev-test';
  }
  const sensorsdata = await loadSensorsdata();
  return sensorsdata?.store.getDistinctId() || (await getAnonymousID()) || 'empty_distinct_id';
}

async function init() {
  // Disable tracking in dev environment
  // if (system.isDev) {
  //   //@ts-ignore
  //   window[`ga-disable-${env.uaID}`] = true;
  //   return false;
  // }
  if (isClient) {
    const sensorsdata = await loadSensorsdata();

    // Set server_url based on environment variable
    const server_url = import.meta.env.VITE_TRACKPOINT_URL || '';

    // console.log('Current env VITE_TRACKPOINT_URL:', server_url);
    sensorsdata?.init({
      // Leave empty; no data will be sent to Sensors
      server_url: server_url,
      // show_log: import.meta.env.DEV, // Only enable logs in dev environment
      show_log: false, // Only enable logs in dev environment
      heatmap: {
        clickmap: 'not_collect',
        // scroll_notice_map: "default",
      },
      source_channel: ['stag'],
      batch_send: true, // Enable batch sending for better performance, enabled by default
      send_type: 'ajax', // Send POST requests via ajax
    });
    // Manually set utm_source
    const query = new URLSearchParams(location.search);
    const utmSourceMatch = document.cookie.match(/utm_source=(.*?);/);
    const utmSource = utmSourceMatch ? utmSourceMatch[1] : '';
    if (!query.has('utm_source') && utmSource) {
      // Delete cookie
      document.cookie = 'utm_source=;expires=Thu, 01 Jan 1970 00:00:01 GMT';
      query.set('utm_source', utmSource);
      // Unknown logic, temporarily disabled
      // history.replaceState(
      //   null,
      //   '',
      //   `${location.pathname}?${query.toString()}`
      // );
      sensorsdata?.store.setProps({ $latest_utm_source: utmSource });
    }
    const { deviceId, channelType } = getBasicInfo();
    // const sensorsDeviceId = await getAnonymousID();

    // Bind common properties
    sensorsdata?.registerPage({
      // TODO: setting, not available yet
      // Need to get language
      lang: () => 'us',
      theme: () => setting.theme,
      system_date: String(new Date().getTimezoneOffset() / -60),
      clientId: import.meta.env.VITE_APP_CLIENT,
      deviceId: deviceId || '',
      // pseudoId,
      frontSessionId: getSessionId(),
      channelType,
    });
    autoTrackException();
  }
}

async function loginUser({ userId, level }: { userId: string; level: string }) {
  const sensorsdata = await loadSensorsdata();
  sensorsdata?.login(String(userId));
  // Bind common properties
  sensorsdata?.registerPage({
    level: () => Number(level),
  });
}

// page_view
Event.safeOn(EventName.PAGE_VIEW, ({ url, referrer }) => {
  trackPageView({
    url,
    referrer,
  });
});

// Bind common data on first registration
Event.safeOn(EventName.REGIST_SUCCESS, ({ userId, level }) => {
  // Bind common properties
  loginUser({ userId: String(userId), level: level ? String(level) : '1' });
  profileSetOnce();
});

Event.safeOn(EventName.LOGIN_SUCCESS, async ({ userId }) => {
  const sensorsdata = await loadSensorsdata();
  sensorsdata?.login(String(userId));
});

// Set user profile on each login
Event.safeOn(EventName.PROFILE_SET, (options) => {
  // Update user properties
  profileSet(options);
});

// Sensors custom events
Event.safeOn(EventName.SENSORS_TRACK, ({ event, ...otherArgs }) => {
  trackEvent(event, otherArgs);
});

export default { init, getDistinctId };
